import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { 
    NeoCard, 
    NeoButton, 
    NeoInput, 
    NeoBadge, 
    NeoAlert, 
    NeoEmptyState 
} from "../../../../components/Common";
import { INTEGRATION_PROVIDERS } from "../constants";

/**
 * IntegrationDetail
 * Painel direito do Master-Detail: formulário de configuração + área de teste.
 * Migrado para o Neo Design System (100% Neo components e zero estilos inline).
 */
const STATUS_CONFIG = {
    open: { label: "Online", icon: "mdi-check-circle-outline" },
    close: { label: "Offline", icon: "mdi-close-circle-outline" },
    connecting: { label: "Conectando...", icon: "mdi-loading mdi-spin" },
    checking: { label: "Verificando...", icon: "mdi-loading mdi-spin" },
    error: { label: "Erro", icon: "mdi-alert-circle-outline" },
};

const IntegrationDetail = ({
    providerId,
    providerData,
    onSave,
    isSaving,
    onTest,
    isTesting,
    testResult,
    onClearTest,
    evolutionStatus,
}) => {
    const provider = INTEGRATION_PROVIDERS.find((p) => p.id === providerId);
    const [formData, setFormData] = useState({});
    const [testInput, setTestInput] = useState("");
    const testResultRef = useRef(null);
    const prevProviderRef = useRef(providerId);

    // Inicializa o form quando muda de provider ou providerData
    useEffect(() => {
        if (providerData) {
            setFormData({
                provider: providerData.provider,
                name: providerData.name || provider?.name || "",
                isActive: providerData.isActive || false,
                config: { ...(providerData.config || {}) },
                id: providerData.id || null,
                createdAt: providerData.createdAt || null,
                createdBy: providerData.createdBy || null,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId, providerData]);

    // Limpa teste apenas quando troca de provider (não em re-renders)
    useEffect(() => {
        if (prevProviderRef.current !== providerId) {
            setTestInput("");
            if (onClearTest) onClearTest();
            prevProviderRef.current = providerId;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId]);

    // Auto-scroll para o resultado quando chegar
    useEffect(() => {
        if (testResult && testResultRef.current) {
            testResultRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [testResult]);

    if (!provider) {
        return (
            <NeoEmptyState
                icon="mdi-puzzle-outline"
                title="Selecione uma integração"
                description="Escolha uma das integrações disponíveis na barra lateral para começar a configurar e conectar seus serviços."
            />
        );
    }

    const handleConfigChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            config: { ...prev.config, [key]: value },
        }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    const handleTestClick = () => {
        onTest(providerId, formData.config, testInput);
    };

    return (
        <NeoCard
            title={provider.name}
            subtitle={provider.description}
            headerIcon={provider.icon}
            headerActions={
                <div className="d-flex align-items-center gap-3">
                    {providerId === "evolution" && evolutionStatus && STATUS_CONFIG[evolutionStatus] && (
                        <NeoBadge color={evolutionStatus === "open" ? "success" : evolutionStatus === "close" ? "danger" : "muted"}>
                            <i className={`mdi ${STATUS_CONFIG[evolutionStatus].icon} me-1`}></i>
                            {STATUS_CONFIG[evolutionStatus].label}
                        </NeoBadge>
                    )}
                    
                    {/* Switch Ativo/Inativo */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <span className={`font-size-12 fw-bold ${formData.isActive ? "text-success" : "text-muted"}`}>
                            {formData.isActive ? "Ativo" : "Inativo"}
                        </span>
                        <div className="form-check form-switch mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input custom-switch-primary cursor-pointer mb-0"
                                checked={formData.isActive || false}
                                onChange={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        isActive: !prev.isActive,
                                    }))
                                }
                                id="activeSwitch"
                            />
                        </div>
                    </div>
                </div>
            }
        >
            <div className="row">
                <div className="col-12 mb-4">
                    <h6 className="fw-bold font-size-14 mb-4 text-dark">
                        <i className="mdi mdi-cog-outline me-2 text-primary"></i>
                        Configuração
                    </h6>

                    {provider.configFields.map((field) => (
                        <div key={field.key} className="mb-3">
                            <NeoInput
                                label={`${field.label}${field.required ? " *" : ""}`}
                                type={field.type || "text"}
                                placeholder={field.placeholder}
                                value={formData.config?.[field.key] || ""}
                                onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                options={field.type === "select" ? field.options.map(opt => ({ value: opt, label: opt })) : []}
                                rows={field.key === "customPrompt" || field.key === "customRules" ? 18 : 4}
                                inputStyle={
                                    field.key === "customPrompt" || field.key === "customRules"
                                        ? { minHeight: "350px", fontFamily: "monospace", fontSize: "13px" }
                                        : {}
                                }
                            />
                        </div>
                    ))}

                    {/* Botão Salvar */}
                    <div className="d-flex justify-content-end pt-3 border-top mb-4 mt-4">
                        <NeoButton
                            variant="primary"
                            loading={isSaving}
                            onClick={handleSubmit}
                            icon="mdi-check"
                        >
                            Salvar Configuração
                        </NeoButton>
                    </div>
                </div>

                {/* Área de Teste */}
                <div className="col-12 border-top pt-4 mt-2">
                    <h6 className="fw-bold font-size-14 mb-3 text-dark">
                        <i className="mdi mdi-flask-outline me-2 text-warning"></i>
                        Testar Integração
                    </h6>
                    <p className="text-muted small mb-4">
                        {providerId === "evolution"
                            ? "Digite um número de WhatsApp para enviar uma mensagem de teste. O status da conexão é monitorado automaticamente."
                            : "Envia uma mensagem de teste para verificar se a API Key e o modelo estão funcionando."}
                    </p>

                    <div className="d-flex gap-2 align-items-start mb-4">
                        <div className="flex-grow-1">
                            <NeoInput
                                type="text"
                                placeholder={provider.testPlaceholder}
                                value={testInput}
                                onChange={(e) => setTestInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleTestClick();
                                }}
                            />
                        </div>
                        <NeoButton
                            variant="warning"
                            loading={isTesting}
                            onClick={handleTestClick}
                            icon="mdi-play-circle-outline"
                        >
                            Testar
                        </NeoButton>
                    </div>

                    {/* Resultado do teste */}
                    <div className="mt-3" ref={testResultRef}>
                        {testResult && (
                            <NeoAlert variant={testResult.success ? "success" : "danger"} className="mb-4">
                                {testResult.success 
                                    ? "Integração testada com sucesso!" 
                                    : "Falha no teste de integração."}
                            </NeoAlert>
                        )}

                        {/* Campo de resposta — sempre visível para OpenAI/Gemini */}
                        {(providerId === "openai" || providerId === "gemini") && (
                            <div className="mb-3">
                                <label className="neo-input-label mb-2 d-flex align-items-center gap-2">
                                    <i className="mdi mdi-message-reply-text-outline"></i>
                                    Resposta da IA
                                    {isTesting && (
                                        <span className="text-warning d-inline-flex align-items-center gap-1" style={{ fontSize: "11px", fontWeight: 500 }}>
                                            <i className="mdi mdi-loading mdi-spin" style={{ fontSize: "13px" }}></i>
                                            Aguardando resposta...
                                        </span>
                                    )}
                                </label>
                                <div 
                                    className="border rounded p-3 font-size-13 custom-scrollbar"
                                    style={{
                                        minHeight: "140px",
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        backgroundColor: testResult?.success 
                                            ? "rgba(40, 167, 69, 0.05)" 
                                            : testResult && !testResult.success 
                                                ? "rgba(220, 53, 69, 0.05)" 
                                                : "#f8f9fa",
                                        borderColor: testResult?.success 
                                            ? "rgba(40, 167, 69, 0.25)" 
                                            : testResult && !testResult.success 
                                                ? "rgba(220, 53, 69, 0.25)" 
                                                : "#dee2e6",
                                        color: testResult && !testResult.success 
                                            ? "#dc3545" 
                                            : testResult?.success 
                                                ? "inherit" 
                                                : "#6c757d"
                                    }}
                                >
                                    {isTesting ? (
                                        <div className="d-flex flex-column gap-2">
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-12" style={{ height: "12px", borderRadius: "4px" }}></span>
                                                <span className="placeholder col-10 mt-1" style={{ height: "12px", borderRadius: "4px" }}></span>
                                                <span className="placeholder col-8 mt-1" style={{ height: "12px", borderRadius: "4px" }}></span>
                                                <span className="placeholder col-6 mt-1" style={{ height: "12px", borderRadius: "4px" }}></span>
                                            </div>
                                        </div>
                                    ) : testResult?.message ? (
                                        testResult.message
                                    ) : (
                                        <span className="fst-italic">
                                            A resposta da IA aparecerá aqui após o teste...
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Para Evolution ou erros em outros providers */}
                        {providerId === "evolution" && testResult && (
                            <NeoAlert variant={testResult.success ? "success" : "danger"} className="mb-4">
                                <div style={{ maxHeight: "150px", overflow: "auto" }}>
                                    {testResult.message}
                                </div>
                            </NeoAlert>
                        )}
                    </div>
                </div>
            </div>
        </NeoCard>
    );
};

IntegrationDetail.propTypes = {
    providerId: PropTypes.string,
    providerData: PropTypes.object,
    onSave: PropTypes.func,
    isSaving: PropTypes.bool,
    onTest: PropTypes.func,
    isTesting: PropTypes.bool,
    testResult: PropTypes.object,
    onClearTest: PropTypes.func,
    evolutionStatus: PropTypes.string,
};

export default IntegrationDetail;
